import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Phone, Mail, GripVertical, Link2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { VENDOR_STAGES_ALL, VENDOR_OUTCOMES, INVESTOR_PIPELINE, stageTone, typeBadge, ukDate, gbp } from "./crmPipelineShared";
import { FollowUpControl } from "./FollowUpControl";
import { useStageMover } from "./useStageMover";
import { SourceBadge } from "./SourceBadge";
import { LoadError, errMsg } from "./LoadError";

function ReferralStats({ refreshKey }) {
  const [st, setSt] = useState(null);
  useEffect(() => { api.get("/crm/referral-stats").then((r) => setSt(r.data)).catch(() => setSt(null)); }, [refreshKey]);
  if (!st) return null;
  const items = [["Referrals made", st.referrals_made, "referrals-made"], ["Instructions signed", st.instructions_signed, "instructions"], ["Listed", st.listed, "listed"],
    ["Sold", st.sold, "sold"], ["Sales completed", st.sales_completed, "completed"], ["Commissions paid (count)", st.commissions_received, "received-count"]];
  return (
    <div className="mb-5 bg-[#0F2E23] rounded-xl border border-white/10 p-4" data-testid="referral-stats">
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        {items.map(([l, v, t]) => <div key={t}><div className="text-xl font-mono text-white" data-testid={`stat-${t}`}>{v}</div><div className="text-[10px] uppercase text-emerald-100/50">{l}</div></div>)}
      </div>
      <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-x-6 gap-y-1 text-xs">
        <span className="text-emerald-100/60">Expected commission (forecast): <b className="text-white font-mono" data-testid="stat-expected">{gbp(st.commission_expected_open)}</b></span>
        <span className="text-emerald-100/60">Earned, not yet paid: <b className="text-white font-mono" data-testid="stat-earned">{gbp(st.commission_earned_unpaid)}</b></span>
        <span className="text-emerald-100/60">Received (actual income): <b className="text-emerald-300 font-mono" data-testid="stat-received">{gbp(st.commission_received)}</b></span>
        {st.referral_date_missing > 0 && <span className="text-amber-300">{st.referral_date_missing} referral(s) missing a referral date</span>}
      </div>
    </div>
  );
}

export default function CrmPipeline() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "investor" ? "investor" : "vendor";
  const stages = view === "investor" ? INVESTOR_PIPELINE : VENDOR_STAGES_ALL;
  const [board, setBoard] = useState(null);
  const [drag, setDrag] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [tick, setTick] = useState(0);

  const load = useCallback(() => {
    setLoadErr("");
    api.get(`/crm/pipeline?view=${view}`).then((r) => { setBoard(r.data); setTick((t) => t + 1); }).catch((e) => setLoadErr(errMsg(e, "Could not load pipeline")));
  }, [view]);
  useEffect(load, [load]);
  const mover = useStageMover({ view, onMoved: load });

  const onStageChange = (card, stage) => {
    if (stage === card._stage) return;
    mover.request({ ...card, hasReferral: card.has_referral }, stage);
  };

  const onDrop = (stage) => {
    if (!drag) return;
    const card = drag;
    setDrag(null);
    onStageChange(card, stage);
  };

  const setCardFollowup = async (card, date) => {
    try {
      await api.post(`/crm/unified-contacts/${card.store}/${card.id}/followup`, { date });
      toast.success(date ? "Follow-up set" : "Follow-up cleared");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set follow-up"); }
  };

  const cols = board?.columns || {};

  return (
    <CrmShell title="Pipeline">
      {mover.dialog}

      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="inline-flex rounded-full bg-[#0F2E23] border border-white/10 p-1" data-testid="pipeline-view-toggle">
          {["vendor", "investor"].map((v) => (
            <button key={v} onClick={() => setParams({ view: v })} data-testid={`pipeline-view-${v}`}
              className={`px-4 py-1.5 text-sm rounded-full capitalize transition ${view === v ? "bg-brass text-emerald-950 font-semibold" : "text-emerald-100/70 hover:text-white"}`}>
              {v} pipeline
            </button>
          ))}
        </div>
        <div className="text-xs text-emerald-100/50 flex items-center gap-1.5"><GripVertical className="w-3.5 h-3.5" /> Drag cards between columns, or use the stage menu on each card</div>
      </div>

      {view === "vendor" && <ReferralStats refreshKey={tick} />}
      {loadErr && <LoadError message={loadErr} onRetry={load} testId="pipeline-load-error" />}
      <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-board">
        {stages.map((stage) => (
          <div key={stage} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(stage)}
            data-testid={`pipeline-col-${stage.replace(/[^a-z]/gi, "-").toLowerCase()}`}
            className={`shrink-0 w-[270px] rounded-xl border flex flex-col ${VENDOR_OUTCOMES.includes(stage) && view === "vendor" ? "bg-black/20 border-dashed border-white/15" : "bg-[#0B241B]/60 border-white/10"}`}>
            <div className={`flex items-center justify-between px-3 py-2.5 border-b border-white/10 rounded-t-xl`}>
              <span className="text-xs font-semibold text-white">{stage}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${stageTone(stage)}`}>{board ? (cols[stage]?.length || 0) : "—"}</span>
            </div>
            <div className="p-2 space-y-2 min-h-[120px] flex-1">
              {(cols[stage] || []).map((c) => {
                const card = { ...c, _stage: stage };
                return (
                  <div key={`${c.store}-${c.id}`} draggable onDragStart={() => setDrag(card)} onDragEnd={() => setDrag(null)}
                    onClick={() => navigate(`/crm/contact/${c.store}/${c.id}`)} data-testid={`pipeline-card-${c.id}`}
                    className="bg-[#0F2E23] rounded-lg border border-white/10 p-2.5 cursor-pointer hover:border-brass/40 active:opacity-70 transition">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm text-white font-medium leading-tight">{c.name}</div>
                      <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full ${typeBadge(c.contact_type)}`}>{c.contact_type === "Professional / Introducer" ? "Pro" : c.contact_type}</span>
                    </div>
                    {view === "vendor" && <div className="mt-1"><SourceBadge source={c.source} testId={`pipeline-source-${c.id}`} /></div>}
                    {c.needs_review && <div className="text-[10px] text-amber-300 flex items-center gap-1 mt-0.5" data-testid={`pipeline-review-${c.id}`}><AlertTriangle className="w-2.5 h-2.5" /> Needs stage review</div>}
                    {c.linked_records > 1 && <div className="text-[10px] text-brass flex items-center gap-1 mt-0.5"><Link2 className="w-2.5 h-2.5" />{c.linked_records} records</div>}
                    <div className="flex flex-col gap-0.5 mt-1.5" onClick={(e) => e.stopPropagation()}>
                      {c.mobile && <a href={`tel:${c.mobile}`} className="flex items-center gap-1 text-[11px] text-emerald-100/60 hover:text-brass"><Phone className="w-2.5 h-2.5" />{c.mobile}</a>}
                      {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-[11px] text-emerald-100/60 hover:text-brass truncate"><Mail className="w-2.5 h-2.5" /><span className="truncate">{c.email}</span></a>}
                    </div>
                    {c.next_follow_up && <div className="text-[10px] text-amber-300/80 mt-1">Follow-up {ukDate(c.next_follow_up)}</div>}
                    {["Lost", "Not Interested", "Withdrawn"].includes(stage) && c.lost_reason && <div className="text-[10px] text-red-300/80 mt-1 italic">{c.lost_reason}</div>}
                    <div className="mt-1.5">
                      <FollowUpControl compact value={c.next_follow_up} onSet={(d) => setCardFollowup(c, d)} testIdPrefix={`pipe-fu-${c.id}`} />
                    </div>
                    <select value={stage} onClick={(e) => e.stopPropagation()} onChange={(e) => onStageChange(card, e.target.value)}
                      data-testid={`pipeline-stage-select-${c.id}`}
                      className="mt-2 w-full bg-[#091E16] border border-white/10 rounded-md px-2 py-1 text-[11px] text-emerald-100/80 outline-none focus:border-brass">
                      {stages.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                );
              })}
              {board && (cols[stage] || []).length === 0 && <div className="text-[11px] text-emerald-100/30 text-center py-6">Empty</div>}
            </div>
          </div>
        ))}
      </div>
    </CrmShell>
  );
}
